import { Button, ButtonShadcn } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import { X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "@/components/ui/AlertDialog";
import { jobfitApplyConsentNote } from "../../constants/consentModeNotes";

// Consent #1 — the recruiter's own attestation, in-app. Accepting navigates to
// the review route; it does not itself write anything.
const AttestationDialog = ({ job, text, checked, submitting, onCheckedChange, onCancel, onConfirm }) => {
  // Radix's AlertDialog deliberately ignores outside clicks — it's built for
  // confirmations that force an explicit choice, with no prop to opt out.
  // This one doesn't need that: cancelling here writes nothing, so dismissing
  // it on a backdrop click like a plain dialog is safe (via AlertDialogContent's
  // `onOverlayClick` escape hatch). Guarded on `submitting` the same way the
  // Cancel button and the X below are, so a click mid-navigation can't fire twice.
  const dismiss = () => {
    if (submitting) return;
    onCancel();
  };

  // Which of the two modes this job runs in, resolved server-side and carried
  // on the row (see get_job_fit). Shown before the attestation, not after the
  // submit: the recruiter is attesting to something different in each mode.
  const consentNote = jobfitApplyConsentNote(job?.require_jobfit_apply_email_consent);

  return (
    <AlertDialog open={!!job} onOpenChange={(open) => !open && onCancel()}>
      <AlertDialogContent className="!max-w-2xl" onOverlayClick={dismiss}>
        <ButtonShadcn
          variant="ghost"
          size="icon"
          onClick={onCancel}
          disabled={submitting}
          aria-label="Close"
          className="absolute right-4 top-4 h-auto w-auto rounded-md p-1 text-text-secondary transition-colors hover:bg-background-tertiary hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          <X size={16} aria-hidden="true" />
        </ButtonShadcn>

        <AlertDialogHeader>
          <AlertDialogTitle className="pr-8">Apply {job?.job_title ? `to "${job.job_title}"` : "to this job"}?</AlertDialogTitle>
          <AlertDialogDescription className="pr-8">
            This reuses the candidate's already-confirmed information — no new resume parsing. You'll review and edit the
            details on the next screen before anything is submitted.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <label className="flex items-start gap-2 rounded-lg border border-border-primary bg-background-secondary px-3 py-2.5 text-sm text-text-primary">
          <Checkbox checked={checked} onCheckedChange={(v) => onCheckedChange(!!v)} className="mt-0.5" />
          <span>{text || "Loading…"}</span>
        </label>

        {consentNote && <p className="text-xs leading-snug text-text-secondary">{consentNote}</p>}

        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel} disabled={submitting}>
            Cancel
          </AlertDialogCancel>
          <Button
            onClick={onConfirm}
            disabled={!checked || submitting}
            className="inline-flex h-8 items-center justify-center rounded-md bg-button-secondary px-4 text-sm font-medium text-text-base disabled:cursor-not-allowed disabled:opacity-50"
          >
            Continue
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default AttestationDialog;
